import { uniq, uniqBy, isEqual } from 'lodash';
import { createSelector } from 'reselect';
import moment from 'moment';
import { openDutyTransactionsByMembershipId } from '../duty-transactions/selectors';
import { overlappingIntervals } from '../../../utils/schedules';
import { cacheArray } from '../../../utils/memoize';

const defaultFunctionId = state => state.dutyPlanner.defaultFunctionId;
const groupId = state => state.dutyPlanner.groupId;
const startTime = state => state.dutyPlanner.startTime;
const endTime = state => state.dutyPlanner.endTime;
const allMemberships = state => state.memberships.byId;
const allUsers = state => state.users.byId;
const allSkills = state => state.skills.byId;
const allAvailabilityCodes = state => state.availabilityCodes.byId;
const membershipCombinedSchedules = state => state.combinedSchedules.full.membership;

export const groupMemberships = createSelector(groupId, allMemberships, (groupId, memberships) =>
  uniq(Object.values(memberships).filter(m => m.group_id === groupId))
);

// Returns a list of users, augmented with their skills, the id of the specific membership
// for the group, and the schedule intervals that contain the performed duty function
const allUsersAugmented = createSelector(
  allUsers,
  groupMemberships,
  allSkills,
  openDutyTransactionsByMembershipId,
  allAvailabilityCodes,
  membershipCombinedSchedules,
  startTime,
  endTime,
  (usersById, memberships, skillsById, dutyTransactions, availabilityCodesById, schedules, startTime, endTime) =>
    memberships
      .filter(m => m.duty === true)
      .map(m => [m, usersById[m.user_id], schedules[m.id]])
      // filter out the memberships which don't have an associated user and
      // schedule
      .filter(([_m, user, schedule]) => user && schedule)
      .map(([membership, user, schedule]) => [
        // add info about membership and skills
        {
          ...user,
          membershipId: membership.id,
          displayOrder: membership.display_order,
          skills: membership.skills.map(id => skillsById[id])
        },
        schedule
      ])
      .map(([user, schedule]) => [
        // This filters the duty transactions that are only contained in the viewing period.
        {
          ...user,
          dutyTransactions: overlappingIntervals(dutyTransactions[user.membershipId] || [], startTime, endTime).map(
            dt => ({
              ...dt,
              availabilityCode: availabilityCodesById[dt.availability_code_id]
            })
          )
        },
        schedule
      ])
      .map(([user, schedule]) => [
        // get the intervals in the current period
        user,
        schedule.intervals.filter(
          i => i.active && moment(i.start_time).isBefore(endTime) && moment(i.end_time).isAfter(startTime)
        )
      ])
      .flatMap(([user, intervalsInPeriod]) =>
        augmentIntervalInformation(user, intervalsInPeriod, availabilityCodesById, startTime, endTime)
      )
);

const augmentIntervalInformationCache = {};

const augmentIntervalInformation = (user, intervalsInPeriod, availabilityCodes, startTime, endTime) => {
  const membershipAssignments = [];

  for (let interval of intervalsInPeriod) {
    const availabilityCode = availabilityCodes[interval.availability_code_id] || null;
    const minEndTime = moment.min(moment(interval.end_time), moment(endTime));
    const maxStartTime = moment.max(moment(interval.start_time), moment(startTime));
    const intervalLength = interval.available ? minEndTime.diff(maxStartTime, 'seconds') : 0;

    interval = {
      ...interval,
      availabilityCode,
      start_time: maxStartTime,
      end_time: minEndTime
    };

    if (interval.assigned_function_ids.length === 0) {
      membershipAssignments.push({
        ...user,
        functionId: null,
        intervals: [interval]
      });
    }

    interval.assigned_function_ids.map(functionId => {
      let existingAssignment = membershipAssignments.find(assignment => assignment.functionId === functionId);

      if (!existingAssignment) {
        existingAssignment = {
          ...user,
          functionId,
          intervals: [],
          availabilityLength: 0
        };
        membershipAssignments.push(existingAssignment);
      }

      existingAssignment.availabilityLength += intervalLength;
      existingAssignment.intervals.push(interval);
    });
  }

  // if the computed value has exactly the same value as the cache, return the
  // cached value. This is to avoid rerenders by ensuring the objects stay the
  // same
  if (isEqual(augmentIntervalInformationCache[user.membershipId], membershipAssignments)) {
    return augmentIntervalInformationCache[user.membershipId];
  }

  return (augmentIntervalInformationCache[user.membershipId] = membershipAssignments);
};

// Filters from all the augmented users those who have a function assigned and
// is exactly the default one
export const candidateUsers = createSelector(allUsersAugmented, defaultFunctionId, (intervalStatuses, functionId) =>
  cacheArray(
    'candidateUsers',
    intervalStatuses.filter(i => i.functionId === functionId)
  )
);

// Filters from all the augmented users those who are available on call
export const usersOnCall = createSelector(allUsersAugmented, allUsers =>
  cacheArray(
    'usersOnCall',
    uniqBy(
      allUsers.filter(u => u.intervals.find(i => i.available && i.assigned_function_ids.length === 0)),
      'id'
    )
  )
);

// Filters from all the augmented users those who have no functions assigned
export const usersOffDuty = createSelector(allUsersAugmented, candidateUsers, (allUsers, functionAssignments) => {
  const ids = uniq(functionAssignments.map(i => i.id));
  // since we have an entry of one user in each of their intervals from the
  // all users augmented selector and here, we only care about
  // displaying the user, just display them once.
  return cacheArray(
    'usersOffDuty',
    uniqBy(
      allUsers.filter(u => !u.functionId && !ids.includes(u.id)),
      'id'
    )
  );
});

// Filters from all the augmented users the ones with a function assigned, but
// not the default one
export const assignedUsers = createSelector(allUsersAugmented, defaultFunctionId, (allUsers, functionId) =>
  cacheArray(
    'assignedUsers',
    allUsers.filter(u => u.functionId && u.functionId != functionId)
  )
);

// This selector is only used by the timeline component. This will be left here
// for now.
export const assignedUsersTimelineItems = createSelector(assignedUsers, usersWithFunctions => {
  // Ensure that the items are generated using the correct locale. The language
  // of the Timeline Component depends on it
  moment.locale((globalThis.I18n || {}).locale);
  const items = [];
  usersWithFunctions.map(user => {
    user.intervals.map(interval => {
      // we filter the duty Transactions that apply within the interval period,
      // these will be used to display the transactions in the timeline
      const dutyTransactions = overlappingIntervals(user.dutyTransactions, interval.start_time, interval.end_time);
      const item = {
        ...interval,
        id: `${user.membershipId}+${interval.start_time}-${interval.end_time}`,
        membership_id: user.membershipId,
        group: interval.assigned_function_ids[0],
        dutyTransactions,
        userNickname: `${user.nickname}`,
        start_time: moment(interval.start_time),
        end_time: moment(interval.end_time)
      };
      items.push(item);
    });
  });
  return cacheArray('assignedUsersTimelineItems', items);
});
